
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import InstrumentEdit from "@/components/instrument/InstrumentEdit.vue";
import {
  alertModule,
  currentUserModule,
  dataFieldsEditModule,
  instrumentModule, queryModule,
  studyDefinitionModule,
  studyModule,
  studyPatientContext,
  studyPatientModule,
  visitModule
} from "@/store";
import InstrumentContextCache from "@/models/instrument.context.cache";
import VisitContextCache from "@/models/visit.context.cache";
import {Dictionary} from "vue-router/types/router";
import {
  convertToCreateDtos,
  convertToDeleteDtos,
  convertToUpdateDtos,
  findRedirectRuleFields
} from "@/auxillary/datafield.cache.helpers";
import {translate} from "@/auxillary/translation";
import YesNoCancelDialog from "@/components/common/YesNoCancelDialog.vue";
import {formatDateFilter} from "@/auxillary/date-format";
import {NavigationGuardNext, Route} from "vue-router";
import {deepCopy} from "@/auxillary";
import InstrumentStateIcon from "@/components/instrument/InstrumentStateIcon.vue";
import {MdbsCore} from "@/api/tsclient";
import DataFieldCache from "@/models/data.field.cache";
import SignatureDialog from "@/components/signature/SignatureDialog.vue";
import {eventBus} from "@/main";
import Rights = MdbsCore.Rights;
import InstrumentDefinitionDto = MdbsCore.InstrumentDefinitionDto;
import ITemporaryDataFieldDto = MdbsCore.ITemporaryDataFieldDto;
import {canSeeInstrumentQueries, canSeeQueries} from '@/components/query/QueryPermissions';
import IQueryTypeDefinitionDto = MdbsCore.IQueryTypeDefinitionDto;
import IQueryCreateDto = MdbsCore.IQueryCreateDto;
import QueryTypeDefinitionDto = MdbsCore.QueryTypeDefinitionDto;
import QueriesMenu from "@/components/query/menu/QueriesMenu.vue";
import QueryCreateMenuItem from "@/components/query/menu/QueryCreateMenuItem.vue";
import QueryCreateDialog from "@/components/query/dialogs/QueryCreateDialog.vue";
import IQueryListResponse = MdbsCore.IQueryListResponse;
import {
  fieldsWithValuesWillBeDeleted, findFieldsWithDeletionUndone,
  IDataFieldDeletionReport
} from "@/auxillary/datafield.deletion";
import DeletedDataFieldsDialog from "@/components/study/dataFieldDeletion/DataFieldsDeletedDialog.vue";
import DataFieldsUndoneDeletionDialog from "@/components/study/dataFieldDeletion/DataFieldsDeletionUndoneDialog.vue";
import {AEREPORTS} from "@/components/study/studyDefinitionIds";
import {canSeeInstrumentAuditTrail} from "@/auxillary/audittrail.permissions";
import InstrumentHints from "@/components/study/instrument/InstrumentHints.vue";
import DataFieldsChangedDialog from "@/components/study/dataFieldChange/DataFieldsChangedDialog.vue";
import {fieldsWithValuesWillBeChanged, IDataFieldChangeReport} from "@/auxillary/datafield.changes";

@Component({
  methods: {canSeeInstrumentAuditTrail},
  components: {
    DataFieldsChangedDialog,
    InstrumentHints,
    DataFieldsUndoneDeletionDialog,
    SignatureDialog,
    InstrumentStateIcon,
    InstrumentEdit,
    YesNoCancelDialog,
    QueriesMenu,
    QueryCreateMenuItem,
    QueryCreateDialog,
    DeletedDataFieldsDialog
  },
  filters: {
    formatDateFilter
  },
  beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext<InstrumentView>) {
    if (dataFieldsEditModule.hasChanges && this.isEditable) {
      this.showSecureLoadDialog = true;
      this.secureLoadRoute = to;
      if (this.stopEventReportContinue) {
        this.stopEventReportContinue = false;
        return false;
      }
      return next(false);
    }
    this.showSecureLoadDialog = false;
    this.secureLoadRoute = undefined;
    dataFieldsEditModule.resetAfterSaveAction();
    dataFieldsEditModule.resetLastEditedFieldId();
    this.changeInstrument();

    return next();
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext<InstrumentView>) {
    if (to.name == "login") return next();

    if (dataFieldsEditModule.hasChanges) {
      this.showSecureLoadDialog = true;
      this.secureLoadRoute = to;
      if (this.stopEventReportContinue) {
        this.stopEventReportContinue = false;
        return false;
      }
      return next(false);
    } else {
      this.showSecureLoadDialog = false;
      this.secureLoadRoute = undefined;
      dataFieldsEditModule.resetAfterSaveAction();
      dataFieldsEditModule.resetLastEditedFieldId();
      if (to.meta?.instrumentView) {
      this.changeInstrument();
      } else {
        dataFieldsEditModule.reset();
        instrumentModule.reset();
      }
    }

    return next();
  }
})
export default class InstrumentView extends Vue {

  @Prop({default: ""}) readonly patientId: string;
  @Prop({default: "", required: false}) readonly visitId: string;
  @Prop({default: "", required: false}) readonly instrumentId: string;
  @Prop({default: "", required: false}) readonly instrumentDefinitionId: string;

  currentInstrumentContext: Readonly<InstrumentContextCache> | null = null;
  currentVisitContext: Readonly<VisitContextCache> | null = null;
  saving = false;
  showSnackbar = false;
  snackbarText = "";
  snackbarColor = "success";
  _loading = false;

  showSecureLoadDialog = false;
  secureLoadRoute: Route | undefined;

  showRedirectDialog = false;
  redirectDialogText = "";
  redirectDialogSubject = "";
  currentRedirectConfig: any = {};

  showDeletionDialog = false;
  deletedFields: Array<IDataFieldDeletionReport> = [];
  saveContinue = false;
  showUndoneDeletionDialog = false;
  showChangeDialog = false;
  changedFields: Array<IDataFieldChangeReport> = [];


  get tempInstrument() {
    return instrumentModule.tempInstrument;
  }

  get loading() {
    return studyPatientModule.isLoading ||
        studyModule.isLoading ||
        studyDefinitionModule.isLoading ||
        studyPatientContext.isLoading ||
        instrumentModule.isLoading ||
        dataFieldsEditModule.isLoading;
  }

  get canSave() {
    return this.canSaveInstruments && this.isEditable;
  }

  get canSaveInstruments() {
    return this.instrumentId != null && (currentUserModule.hasRight(Rights.InstrumentCreateCmd) ||
        currentUserModule.hasRight(Rights.InstrumentUpdateCmd));
  }

  get isEditable() {
    return (this.currentInstrumentContext?.isEditable ?? false);
  }

  get canNotSaveInstrument() {
    return !this.isEditable || !((this.instrumentId == null && currentUserModule.hasRight(Rights.InstrumentCreateCmd)) ||
        (this.instrumentId != null && currentUserModule.hasRight(Rights.InstrumentUpdateCmd)));
  }

  get showNotEditableReason() {
    return (this.currentInstrumentContext?.isEditable === false) ?? false;
  }

  get notEditableReason() {
    return this.currentInstrumentContext?.notEditableReason ?? "";
  }

  get isSaveButtonVisible() {
    return this.currentInstrumentContext?.identifier != "aeReports";
  }

  get backButtonVisible() {
    return this.currentVisitContext == null;
  }

  get previousButtonVisible() {
    return this.currentVisitContext != null && this.currentVisitContext?.visibleInstruments
        ?.findIndex(i => i.id === this.currentInstrumentContext?.id) > 0;
  }

  get nextButtonVisible() {
    return this.currentVisitContext != null && this.currentVisitContext?.visibleInstruments
        ?.findIndex(i => i.id === this.currentInstrumentContext?.id) < this.currentVisitContext?.visibleInstruments?.length - 1;
  }

  get visits() {
    return studyPatientContext.visibleVisits?.filter(v => !v.isNew && !v.isNotDone) ?? []
  }

  get intermediateVisits() {
    return studyPatientContext.visibleIntermediateVisits?.filter(v => !v.isNew && !v.isNotDone)
            .sort((x, y) => ((x?.visitDate ? x.visitDate.getTime() : 0) - (y?.visitDate ? y.visitDate.getTime() : 0)))
        ?? []
  }

  get instrumentContextId() {
    return this.currentInstrumentContext?.id ?? "";
  }

  get instrumentDefinitionType() {
    return "Instrument";
  }

  get instrumentDisplayName() {
    return this.currentInstrumentContext?.displayName ?? "";
  }

  get visitDisplayName() {
    return this.currentVisitContext?.displayName ?? "";
  }

  get isEmpty() {
    return !this.isNewInstrument && this.instrumentId == null
  }

  get patientDisplayName() {
    return studyPatientContext.studyPatient?.displayName ?? "";
  }

  get toolbarInstruments() {
    return studyPatientContext.standaloneInstruments.filter(i => i.isVisible);
  }

  get hasAnyToolbarInstruments() {
    return studyPatientContext.standaloneInstruments.filter(i => i.isVisible).length > 0;
  }

  get isNewInstrument() {
    return instrumentModule.instrument.id == null;
  }

  async mounted() {
    await studyPatientModule.loadById(this.patientId ?? "")
    await studyModule.loadById(studyPatientModule?.studyPatient?.studyId ?? "")
    await studyDefinitionModule.loadById(studyModule.study?.studyDefinitionId ?? "")
    dataFieldsEditModule.setEditable(this.canSave);
    await this.changeInstrument(undefined);

    // unregister old events - necessary if instrument view is opened multiple times
    eventBus.$off("save-instrument");
    eventBus.$off("reload-instrument");

    // register events
    eventBus.$on("save-instrument", async () => {
      await this.checkForFieldDeletionBeforeSave(false);
    })
    eventBus.$on("reload-instrument", async () => {
      await this.changeInstrument();
    })
  }

  async openStandaloneInstrument(instrumentContext: Readonly<InstrumentContextCache>) {
    if (instrumentContext.isNew) {
      await this.navigate("standaloneinstrumentnew", {
        patientId: this.patientId,
        instrumentDefinitionId: instrumentContext.definitionId,
      });
    } else {
      await this.navigate("standaloneinstrumentedit", {
        patientId: this.patientId,
        instrumentId: instrumentContext.id,
        instrumentDefinitionId: instrumentContext.definitionId
      });
    }
  }

  async openVisitInstrument(visitContext: Readonly<VisitContextCache>, instrumentContext: Readonly<InstrumentContextCache>) {
    if (instrumentContext.isNew) {
      await this.navigate("visitinstrumentnew", {
        patientId: this.patientId,
        visitId: visitContext.id,
        instrumentDefinitionId: instrumentContext.definitionId
      });
    } else {
      await this.navigate("visitinstrumentedit", {
        patientId: this.patientId,
        visitId: visitContext.id,
        instrumentId: instrumentContext.id
      });
    }
  }

  async openPreviousInstrument() {
    if (!this.currentVisitContext)
      return;
    const previousIndex = Math.max(this.currentVisitContext.instruments
        ?.findIndex(i => i.id === this.currentInstrumentContext?.id) - 1, 0);

    await this.openVisitInstrument(this.currentVisitContext, this.currentVisitContext.instruments[previousIndex]);
  }

  async goBack() {
    if (!this.currentVisitContext) {
      this.$router.back();
      await this.changeInstrument();
    }
  }

  async openNextInstrument() {
    if (!this.currentVisitContext)
      return;
    const instruments = this.currentVisitContext.instruments.filter(i => i.isVisible);
    const nextIndex = Math.min(instruments
        ?.findIndex(i => i.id === this.currentInstrumentContext?.id) + 1, instruments.length - 1);

    await this.openVisitInstrument(this.currentVisitContext, instruments[nextIndex]);
  }

  async cancel() {
    await this.openPatientView()
  }

  async checkForFieldDeletionBeforeSave(saveWithContinue: boolean) {
    if (this.showSecureLoadDialog)
      this.showSecureLoadDialog = false;
    this.saveContinue = saveWithContinue;

    const deletedFields = fieldsWithValuesWillBeDeleted( dataFieldsEditModule.dataFields);
    if (deletedFields.length > 0){
      this.showDeletionDialog = true;
      this.deletedFields = deletedFields;
    } else {
       await this.saveAfterDeletionCheck();
    }
  }

  async saveAfterDeletionCheck() {
    this.showDeletionDialog = false;
    console.log("check saveAfterDeletionCheck")
    await this.checkForFieldChangeBeforeSave();
  }

  async checkForFieldChangeBeforeSave() {
    if (this.showSecureLoadDialog)
      this.showSecureLoadDialog = false;

    const changedFields = fieldsWithValuesWillBeChanged( dataFieldsEditModule.dataFields);
    if (changedFields.length > 0){
      this.showChangeDialog = true;
      this.changedFields = changedFields;
    } else {
      await this.saveAfterChangeCheck();
    }
  }

  async saveAfterChangeCheck() {
    this.showChangeDialog = false;
    if (this.saveContinue) {
      await this.saveInstrumentAndContinue();
      this.saveContinue = false;
    }
    else
      await this.saveInstrument();
  }

  async saveInstrument() {
    this.saving = true;

    // eslint-disable-next-line no-unreachable
    await this.saveOrUpdateInstrument();
    if (this.stopEventReportContinue) {
      return;
    }

    if (instrumentModule.instrument.id && instrumentModule.instrument.canSave) {
      await this.changeInstrument(instrumentModule.instrument.id);
      dataFieldsEditModule.setValidationMessages([]);
      return;
    }

    const messages = deepCopy(instrumentModule.instrument?.validationMessages ?? [])
    dataFieldsEditModule.setValidationMessages(messages);
  }

  private stopEventReportContinue = false;

  private async saveOrUpdateInstrument() {
    try {
      const instrumentDefinition = this.currentInstrumentContext?.definition;

      if (instrumentDefinition === undefined) {
        await alertModule.warning("Definition of instrument missing")
        this.saving = false;
        return
      }

      if (this.currentInstrumentContext?.isNew) { // TODO: isNew is false on new instrument coming from aeReports save
        await this.saveNewInstrument(instrumentDefinition);
      } else {
        if (instrumentDefinition.id == AEREPORTS.ID) {
          this.showSecureLoadDialog = false;
          this.saving = false
          this.stopEventReportContinue = true;
          await eventBus.$emit("save-open-report");
          return;
        } else {
          await this.updateInstrument(instrumentDefinition);
        }
      }

      if (instrumentModule.instrument.canSave) {
        this.showSucceededSnackBar(instrumentDefinition.displayKey)
      } else {
        this.showFailedSnackBar(instrumentDefinition.displayKey)
      }

      this.showSnackbar = true;
      this.showSecureLoadDialog = false;

      this.checkShowRedirectDialog();
    } finally {
      this.saving = false
    }
  }

  private setCurrentRedirectConfig(
      field: DataFieldCache,
      isStandaloneInstrument: boolean,
      instrumentContextCache: Readonly<InstrumentContextCache>,
      visitContextCache: Readonly<VisitContextCache> | undefined) {
    this.currentRedirectConfig = {field, isStandaloneInstrument, instrumentContextCache, visitContextCache}
  }

  @Watch('currentRedirectConfig', {immediate: true, deep: true})
  private onRedirectConfigChanged() {
    if (this.currentRedirectConfig?.field === undefined) {
      this.redirectDialogText = "";
      return;
    }

    this.redirectDialogSubject = translate(this.currentRedirectConfig.field.redirectRule?.definition?.subjectDisplayKey);
    this.redirectDialogText = translate(this.currentRedirectConfig.field.redirectRule?.definition?.textDisplayKey);
  }

  private checkShowRedirectDialog() {
    this.currentRedirectConfig = {};

    const redirectFields = findRedirectRuleFields(dataFieldsEditModule.dataFields);
    if (redirectFields.length == 0) return;

    const sortedRedirectFields = redirectFields.sort(function( field1: DataFieldCache, field2: DataFieldCache ){
      return (field2.redirectRule?.definition?.priority ?? 0) - (field1.redirectRule?.definition?.priority ?? 0);
    });

    for (let field of sortedRedirectFields) {
      const targetVisitIdentifier = field.redirectRule?.definition?.targetVisitIdentifier;
      const targetInstrumentIdentifier = field.redirectRule?.definition?.targetInstrumentIdentifier;

      const visitContextCache = targetVisitIdentifier
          ? studyPatientContext.visits.find(v => v.identifier == targetVisitIdentifier)
          : undefined;

      if (visitContextCache) {
        if (visitContextCache.isNew) continue;

        const instrumentContextCache = visitContextCache.visibleInstruments.find(i => i.identifier == targetInstrumentIdentifier);
        if (!instrumentContextCache) continue;

        if (!field.isRedirectVisible) continue;

        this.setCurrentRedirectConfig(field, false, instrumentContextCache, visitContextCache);
        this.showRedirectDialog = true;
        return;
      }

      const instrumentContextCache = studyPatientContext.standaloneInstruments.find(i => i.identifier == targetInstrumentIdentifier);
      if (!instrumentContextCache) continue;

      if (!field.isRedirectVisible) continue;

      this.setCurrentRedirectConfig(field, true, instrumentContextCache, undefined);
      this.showRedirectDialog = true;
      return;
    }
  }

  private async applyRedirectRule() {
    this.showRedirectDialog = false;

    if (!this.currentRedirectConfig.field) {
      return;
    }

    const {isStandaloneInstrument, instrumentContextCache, visitContextCache} = this.currentRedirectConfig;
    if (isStandaloneInstrument) {
      await this.openStandaloneInstrument(instrumentContextCache);
      return;
    }

    await this.openVisitInstrument(visitContextCache, instrumentContextCache);
    return;
  }

  /**
   *  Update existing instrument
   */
  private async updateInstrument(instrumentDefinition: InstrumentDefinitionDto) {
    const fields = convertToUpdateDtos(dataFieldsEditModule.dataFields)
    const deletedFields = convertToDeleteDtos(dataFieldsEditModule.dataFields)
    await instrumentModule.update({
      id: instrumentModule.instrument.id,
      displayKey: instrumentDefinition.displayKey,
      identifier: instrumentDefinition.identifier,
      lockedManually: instrumentModule.instrument.lockedManually,
      order: instrumentModule.instrument.order,
      visitId: instrumentModule.instrument.visitId,
      fields: fields,
      deletedFields: deletedFields

    })
  }

  /**
   *  Create and save instrument
   */
  private async saveNewInstrument(instrumentDefinition: InstrumentDefinitionDto) {
    const fields = convertToCreateDtos(dataFieldsEditModule.dataFields)
    await instrumentModule.create({
      patientId: this.patientId,
      visitId: this.currentVisitContext?.id,
      definitionId: instrumentDefinition.id,
      displayKey: instrumentDefinition.displayKey,
      identifier: instrumentDefinition.identifier,
      order: instrumentDefinition.order,
      fields
    });

    // if saving attempt failed, the returning dto needs to be shown with their errors
    if (!instrumentModule.instrument.canSave && this.currentInstrumentContext?.isNew) {
      dataFieldsEditModule.setEditable(this.canSave)
      const oldTempFields = [...dataFieldsEditModule.temporaryFields];
      await dataFieldsEditModule.loadDataFields({
        definitions: instrumentDefinition?.fields ?? [],
        dataFields: instrumentModule.instrument?.fields ?? [],
        validationMessages: instrumentModule.instrument?.validationMessages,
        temporaryFields: oldTempFields
      });
    }
  }

  public async changeInstrument(instrumentId?: string) {
    Vue.nextTick(async () => {

      dataFieldsEditModule.reset();
      await studyPatientContext.loadById(this.patientId);
      // check if instrument already exists and route to /edit
      // is needed if back-button is used if origin is a new created instrument
      if (!instrumentId && (!this.instrumentId || this.instrumentId == "")) {
        let result = await this.findAndGoToExistingInstrument();
        if (result) return;
      }

      if (!instrumentId) {
        instrumentId = this.instrumentId;
      }

      // load instrument data if given
      this._loading = true;

      if (instrumentId) {
        await instrumentModule.loadById(instrumentId);
        if (instrumentId != undefined && canSeeInstrumentQueries())
          await this.loadQueries(instrumentId);

        if (this.visitId) {
          this.currentVisitContext = studyPatientContext.visitById(this.visitId) ?? null
          this.currentInstrumentContext = this.currentVisitContext?.instruments
              ?.find(i => i.id == instrumentId) ?? null;
        } else {
          this.currentVisitContext = null;
          this.currentInstrumentContext = studyPatientContext.standaloneInstruments
              ?.find(i => i.id == instrumentId) ?? null;
        }
      } else if (this.instrumentDefinitionId) {
        await instrumentModule.reset()
        if (this.visitId) {
          this.currentVisitContext = studyPatientContext.visitById(this.visitId) ?? null
          this.currentInstrumentContext = this.currentVisitContext?.instruments
              ?.find(i => i.definitionId == this.instrumentDefinitionId) ?? null;
        } else {
          this.currentVisitContext = null;
          this.currentInstrumentContext = studyPatientContext.standaloneInstruments
              ?.find(i => i.definitionId == this.instrumentDefinitionId) ?? null;
        }


      } else {
        await instrumentModule.reset()

        this.currentVisitContext = studyPatientContext.visitById(this.visitId) ?? null
        this.currentInstrumentContext = this.currentVisitContext?.instruments[0] ?? null;

        if (!this.currentInstrumentContext?.isNew) {
          await instrumentModule.loadById(this.currentInstrumentContext?.id ?? "")
          if (this.currentInstrumentContext?.id != undefined && canSeeInstrumentQueries())
            await this.loadQueries(this.currentInstrumentContext?.id);
        }
      }

      const instrumentDefinition = studyDefinitionModule
          .instrumentDefinitionById(this.currentInstrumentContext?.definitionId ?? "")

      let tempFields: Array<ITemporaryDataFieldDto> = [];
      if (this.canSave) {
        await instrumentModule.loadTempInstrument({
          patientId: this.patientId, visitId: this.visitId, instrumentDefinitionId: instrumentDefinition?.id!
        });
        tempFields = this.tempInstrument.fields ?? [];
      }

      dataFieldsEditModule.setEditable(this.canSave)
      await dataFieldsEditModule.loadDataFields({
        definitions: instrumentDefinition?.fields ?? [],
        dataFields: instrumentModule.instrument?.fields ?? [],
        validationMessages: instrumentModule.instrument?.validationMessages,
        temporaryFields: tempFields
      });

      const messages = deepCopy(instrumentModule.instrument?.validationMessages ?? [])
      dataFieldsEditModule.setValidationMessages(messages);

      this._loading = false;
    });
  }

  private async findAndGoToExistingInstrument() {
    // visit instrument
    if (this.visitId) {
      let editInstrument = this.instrumentDefinitionId ?
          studyPatientContext?.visitById(this.visitId)?.instruments?.find(
              i => !i.isDeleted && !i.isDefinitionDeleted &&
                  i.definitionId == this.instrumentDefinitionId && !i.isNew)
          : null;

      if (editInstrument) {
        await this.navigate("visitinstrumentedit", {
          patientId: this.patientId,
          visitId: this.visitId,
          instrumentId: editInstrument.id,
          instrumentDefinitionId: editInstrument.definitionId
        });
        return true;
      }
    }

    // standalone instrument
    if (!this.visitId) {
      let editInstrument = this.instrumentDefinitionId ?
          studyPatientContext?.standaloneInstruments?.find(
              i => !i.isDeleted && !i.isDefinitionDeleted &&
                  i.definitionId == this.instrumentDefinitionId && !i.isNew)
          : null;

      if (editInstrument) {
        await this.navigate("standaloneinstrumentedit", {
          patientId: this.patientId,
          instrumentId: editInstrument.id,
          instrumentDefinitionId: editInstrument.definitionId
        });
        return true;
      }
    }
    return false;
  }

  /**
   Prepare new visit- or standalone instrument. Also load pre populated values.
   */
  private async loadInstrumentByDefinition() {
    await instrumentModule.reset()

    // visit instrument
    if (this.visitId) {
      this.currentVisitContext = studyPatientContext.visitById(this.visitId) ?? null
      this.currentInstrumentContext = this.currentVisitContext
          ?.instruments
          ?.find(i => i.definitionId == this.instrumentDefinitionId) ?? null;

      await instrumentModule.loadTempInstrument({
        patientId: this.patientId, visitId: this.visitId, instrumentDefinitionId: this.instrumentDefinitionId
      });

      return;
    }

    // standalone instrument
    this.currentVisitContext = null;
    this.currentInstrumentContext = studyPatientContext
        .standaloneInstruments
        ?.find(i => i.definitionId == this.instrumentDefinitionId) ?? null;

    await instrumentModule.loadTempInstrument({
      patientId: this.patientId, visitId: this.visitId, instrumentDefinitionId: this.instrumentDefinitionId
    });
  }

  private async loadInstrumentById(instrumentId: string) {
    await instrumentModule.loadById(instrumentId);
    if (instrumentId != undefined && canSeeInstrumentQueries())
      await this.loadQueries(instrumentId);

    if (this.visitId) {
      this.currentVisitContext = studyPatientContext.visitById(this.visitId) ?? null
      this.currentInstrumentContext = this.currentVisitContext
          ?.instruments
          ?.find(i => i.id == instrumentId) ?? null;

      return;
    }

    this.currentVisitContext = null;
    this.currentInstrumentContext = studyPatientContext
        .standaloneInstruments
        ?.find(i => i.id == instrumentId) ?? null;
  }

  public async saveInstrumentAndContinue() {
    this.saving = true;

    await this.saveOrUpdateInstrument();
    if (this.stopEventReportContinue)
      return;

    if (instrumentModule.instrument.canSave) {
      await this.continueWithoutSaving();
      dataFieldsEditModule.setValidationMessages([])
    } else {
      const messages = deepCopy(instrumentModule.instrument?.validationMessages ?? [])
      dataFieldsEditModule.setValidationMessages(messages);
    }
  }

  public async checkForDeletionUndone() {
    const deletedFields = findFieldsWithDeletionUndone(dataFieldsEditModule.dataFields);
    if (deletedFields.length > 0) {
      this.showUndoneDeletionDialog = true;
      this.deletedFields = deletedFields;
    } else {
      await this.continueWithoutSaving();
    }
  }

  public async continueWithoutSaving() {
    dataFieldsEditModule.reset();

    const route = await this.navigate(
        this.secureLoadRoute?.name ?? "",
        this.secureLoadRoute?.params ?? {}
    );
    if (route?.meta?.instrumentView) {
      this.showSecureLoadDialog = false;
      await this.changeInstrument();
    }
  }

  async openPatientView() {
    await this.navigate("patient", {patientId: this.patientId})
  }

  async navigate(name: string, params: Dictionary<string>) {
    try {
      return await this.$router.push({name, params});
    } catch (error) {
      if (error.name !== 'NavigationDuplicated')
        throw error;
    }
  }

  showSucceededSnackBar(displayKey: string | undefined) {
    this.snackbarColor = "success";
    this.snackbarText = `<strong>${translate(displayKey)}</strong><br/>Alle Änderungen wurden gespeichert`;
    this.showSnackbar = true;
  }

  showFailedSnackBar(displayKey: string | undefined) {
    if (instrumentModule.instrument?.validationMessages?.some(v => v.messageType == "error"))
      this.snackbarColor = "error";
    else
      this.snackbarColor = "warning";
    this.snackbarText = `<strong>${translate(displayKey)}</strong><br/>Invalide Daten, die Änderungen konnten nicht gespeichert werden`;
    this.showSnackbar = true;
  }

  canSignVisit(visit: Readonly<VisitContextCache>) {
    return !this.isVisitSigned(visit) && this.canSignVisits && visit.isEditable && this.isPatientIncluded
  }

  get canSignVisits() {
    return currentUserModule.hasRight(Rights.VisitSignatureCmd);
  }

  get isPatientIncluded() {
    return studyPatientContext.studyPatient.isIncluded ?? false;
  }

  private showSignatureDialog = false;
  private signatureVisit: Readonly<VisitContextCache> | null = null;

  private isVisitSigned(visit: Readonly<VisitContextCache>) {
    return visit.isSigned;
  }

  private openSignatureDialog(visit: Readonly<VisitContextCache>) {
    this.signatureVisit = visit;
    this.showSignatureDialog = true;
  }

  private async handleSaveSignature() {
    this.showSignatureDialog = false;
    await this.changeInstrument(instrumentModule.instrument.id);

    if (visitModule.visitSignatureResult.success) {
      this.showSucceededSnackBar("Visite signiert");
      return;
    }

    this.showFailedSnackBar("Prüfen Sie die Anmeldedaten des signierenden Nutzers");
  }

  // QUERIES
  showCreateQuery = false;
  createQueryDefinition: IQueryTypeDefinitionDto | null = null;

  get myVisibleQueryTypeDefinitions(): Array<QueryTypeDefinitionDto> {
    return studyDefinitionModule.myVisibleQueryTypeDefinitions;
  }

  get myCreatableQueryTypeDefinitions(): Array<QueryTypeDefinitionDto> {
    return studyDefinitionModule.myCreatableQueryTypeDefinitions;
  }

  get hasCreatableQueryDefinitions() {
    return canSeeQueries && this.myCreatableQueryTypeDefinitions.length > 0;
  }

  get instrumentQueries(): IQueryListResponse {
    return queryModule.instrumentQueryListResponse;
  }

  get hasVisibleQueries() {
    return canSeeInstrumentQueries && (this.instrumentQueries.items?? []).length > 0;
  }

  async loadQueries(instrumentId: string) {
    await queryModule.loadInstrumentQueries(instrumentId);
    await queryModule.loadQueriesForFields({referenceType: "InstrumentDefinition", referenceId: instrumentId});
  }

  handleCreateQuery(queryDefinition: IQueryTypeDefinitionDto) {
    this.createQueryDefinition = deepCopy(queryDefinition);
    this.showCreateQuery = true;
  }

  async handleSaveCreateQuery(queryCreate: IQueryCreateDto) {
    await queryModule.createQueryForInstrument({instrumentId: this.instrumentContextId, createDto:queryCreate})
    this.showCreateQuery = false;
  }

  async openAuditTrail(){
    if (this.visitId) {
      await this.navigate("visitinstrumentaudittrail", {
        patientId: this.patientId,
        visitId: this.currentVisitContext?.id ?? "",
        instrumentId: this.currentInstrumentContext?.id ?? ""
      });
    }
    else {
      await this.navigate("standaloneinstrumentaudittrail", {
        patientId: this.patientId,
        instrumentId: this.currentInstrumentContext?.id ?? ""
      });
    }
  }
}
